import { DotLegalDatePicker, DotLegalFullScreenSpinner, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { Fragment } from "react";
import ProcessingActivityReportDialogHeader from "../processingActivityReportDialogHeader/ProcessingActivityReportDialogHeader";
import { ProcessingActivityStatusManagement } from "../../auth/userContextProvider/UserContextProvider.types";
import { useTranslation } from "../../localization/useTranslation";
import { BodyTypography, DateBox, getDataBasis, HeaderBodyTypography, HeaderBox, TextBoxWrapper } from "../ProcessingActivityReport";
import { useProcessingActivityReportHooks } from "../ProcessingActivityReport.hooks";
import { CustomerReportType } from "../../customer/addEditCustomerDialog/AddEditCustomer.types";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { ProcessingActivityStatus } from "../../processingActivity/processingActivities/ProcessingActivities.types";
import { getNumberOfDataSubjects } from "../../processingActivity/processingActivityOverview/ProcessingActivityOverview.hooks";
import SpanBox from "../SpanBox";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";

export interface IRegistrationOfResearchProjectReport {
    processingActivityId?: string;
    isPDF: boolean;
    onValidChange: (isValid: boolean) => void;
    showError: boolean;
    processingActivityStatus: ProcessingActivityStatus | undefined;
}

export default function RegistrationOfResearchProjectReport(props: IRegistrationOfResearchProjectReport) {
    const { translateString, translateDate, getDateTimePickerLanguage, translateDataSubjectsEnum } = useTranslation();
    const { isLoading, data, onReportChange, permissions } = useProcessingActivityReportHooks(
        CustomerReportType.RegistrationOfResearchProject,
        props.processingActivityId,
        props.onValidChange
    );

    const displayEndDateError = props.showError && (data?.endDate === null || data?.endDate === undefined);
    const disableMandatoryFields =
        permissions.processingActivityStatusManagement === ProcessingActivityStatusManagement.Draft &&
        props.processingActivityStatus !== ProcessingActivityStatus.Draft &&
        props.processingActivityStatus !== ProcessingActivityStatus.AwaitingResearcher;

    return (
        <div>
            {isLoading ? (
                <Box height={"70vh"}>
                    <DotLegalFullScreenSpinner />
                </Box>
            ) : (
                <Box sx={{ paddingLeft: "8px", paddingRigth: "8px" }}>
                    {props.isPDF && <ProcessingActivityReportDialogHeader reportType={CustomerReportType.RegistrationOfResearchProject} isPdf />}
                    <Box sx={{ marginTop: props.isPDF ? 2 : 0 }}>
                        <HeaderBox>
                            <BodyTypography>
                                <HeaderBodyTypography fontSize={16}>{translateString("projectTitel") + ": "}</HeaderBodyTypography>
                                <SpanBox fontSize={16}>{data?.processingActivityName}</SpanBox>
                            </BodyTypography>
                        </HeaderBox>
                    </Box>
                    <HeaderBox>
                        <HeaderBodyTypography>{translateString("approvelNumber") + ": "}</HeaderBodyTypography>
                        <BodyTypography>{data?.approvelNumber}</BodyTypography>
                    </HeaderBox>
                    <HeaderBox>
                        <BodyTypography>
                            <HeaderBodyTypography>{translateString("groupEntities") + ": "}</HeaderBodyTypography>
                            <SpanBox>{data?.groupCompanies.join(", ")}</SpanBox>
                        </BodyTypography>
                    </HeaderBox>
                    <div className="page-break-inside">
                        <HeaderBox>
                            <BodyTypography>
                                <HeaderBodyTypography>{translateString("description") + ": "}</HeaderBodyTypography>
                                <SpanBox>
                                    <MentionInputRender value={data?.description ?? ""} />
                                </SpanBox>
                            </BodyTypography>
                        </HeaderBox>
                    </div>
                    <HeaderBox>
                        <BodyTypography>
                            <HeaderBodyTypography>{translateString("purposes") + ": "}</HeaderBodyTypography>
                            <SpanBox>{data?.purposes.join(", ")}</SpanBox>
                        </BodyTypography>
                    </HeaderBox>
                    <HeaderBox>
                        <HeaderBodyTypography>{translateString("NumberOfSubjects") + ": "}</HeaderBodyTypography>
                        <BodyTypography>
                            {data && data?.numberOfSubjects
                                ? getNumberOfDataSubjects(translateDataSubjectsEnum, data?.numberOfSubjects, data?.noOfSubjects)
                                : undefined}
                        </BodyTypography>
                    </HeaderBox>
                    {props.isPDF ? (
                        <HeaderBox>
                            <HeaderBodyTypography>{translateString("approvelDate") + ": "}</HeaderBodyTypography>
                            <BodyTypography>{data?.approvelDate ? translateDate(data?.approvelDate!) : ""}</BodyTypography>
                        </HeaderBox>
                    ) : (
                        <Box>
                            <HeaderBodyTypography>{translateString("approvelDate") + ": "}</HeaderBodyTypography>
                            <DateBox>
                                <DotLegalDatePicker
                                    label=""
                                    value={data?.approvelDate ?? null}
                                    onChange={(date) => {
                                        const tempData = { ...data! };

                                        if (date === null || isNaN(date!.getTime())) tempData.approvelDate = null;
                                        else {
                                            tempData.approvelDate = date!;
                                        }

                                        onReportChange(tempData);
                                    }}
                                    disabled={permissions.processingActivityStatusManagement! !== ProcessingActivityStatusManagement.Full}
                                    language={getDateTimePickerLanguage()}
                                    noMargin
                                />
                            </DateBox>
                        </Box>
                    )}
                    {props.isPDF ? (
                        <HeaderBox>
                            <HeaderBodyTypography>{translateString("dataprocessorEndDate") + ": "}</HeaderBodyTypography>
                            <BodyTypography>{data?.endDate ? translateDate(data?.endDate!) : ""}</BodyTypography>
                        </HeaderBox>
                    ) : (
                        <Box>
                            <HeaderBodyTypography>{translateString("dataprocessorEndDate") + ": "}</HeaderBodyTypography>
                            <DateBox>
                                <DotLegalDatePicker
                                    label=""
                                    value={data?.endDate ?? null}
                                    onChange={(date) => {
                                        const tempData = { ...data! };

                                        if (date === null || isNaN(date!.getTime())) tempData.endDate = null;
                                        else tempData.endDate = date!;

                                        onReportChange(tempData);
                                    }}
                                    language={getDateTimePickerLanguage()}
                                    noMargin
                                    error={displayEndDateError}
                                    helperMessage={displayEndDateError ? translateString("fieldRequired") : undefined}
                                    disabled={disableMandatoryFields}
                                />
                            </DateBox>
                        </Box>
                    )}
                    <Box className="avoid-page-break" sx={{ marginBottom: props.isPDF ? 1 : "inherit" }}>
                        {props.isPDF ? (
                            <BodyTypography>
                                <HeaderBodyTypography>{translateString("dataBaseAndSources") + ": "}</HeaderBodyTypography>

                                {getDataBasis(translateString, data)}

                                <SpanBox>{data?.note}</SpanBox>
                            </BodyTypography>
                        ) : (
                            <Fragment>
                                <Box>
                                    <HeaderBodyTypography>{translateString("dataBaseAndSources") + ": "}</HeaderBodyTypography>
                                </Box>

                                {getDataBasis(translateString, data)}

                                <DotLegalTextField
                                    multiline
                                    isLoading={isLoading}
                                    debounce
                                    rows={props.isPDF ? undefined : 8}
                                    label={props.isPDF ? "" : translateString("note")}
                                    value={data?.note ?? ""}
                                    onChange={(value) => {
                                        let tempData = { ...data! };
                                        tempData.note = value;
                                        onReportChange(tempData);
                                    }}
                                />
                            </Fragment>
                        )}
                    </Box>
                    <div className="avoid-page-break">
                        <Grid container spacing={1} marginTop={0}>
                            <Grid size={6}>
                                <Fragment>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("primaryResponsible")}</HeaderBodyTypography>
                                    </Box>
                                    {props.isPDF ? (
                                        <BodyTypography>
                                            <SpanBox>{data?.primaryResponsible}</SpanBox>
                                        </BodyTypography>
                                    ) : (
                                        <TextBoxWrapper>
                                            <DotLegalTextField
                                                label={""}
                                                value={data?.primaryResponsible ?? ""}
                                                debounce
                                                multiline
                                                placeholder={translateString("contactPersonPlaceHolder")}
                                                rows={8}
                                                onChange={(text) => {
                                                    let tempModel = { ...data! };
                                                    tempModel.primaryResponsible = text;
                                                    onReportChange(tempModel);
                                                }}
                                                errorText={
                                                    props.showError && isNullOrWhitespace(data?.primaryResponsible ?? undefined)
                                                        ? translateString("fieldRequired")
                                                        : undefined
                                                }
                                                disabled={disableMandatoryFields}
                                            />
                                        </TextBoxWrapper>
                                    )}
                                </Fragment>
                            </Grid>
                            <Grid size={6}>
                                <Fragment>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("contact")}</HeaderBodyTypography>
                                    </Box>
                                    {props.isPDF ? (
                                        <BodyTypography>
                                            <SpanBox>{data?.contact}</SpanBox>
                                        </BodyTypography>
                                    ) : (
                                        <TextBoxWrapper>
                                            <DotLegalTextField
                                                onChange={(text) => {
                                                    let tempModel = { ...data! };
                                                    tempModel.contact = text;
                                                    onReportChange(tempModel);
                                                }}
                                                label={""}
                                                placeholder={translateString("contactPersonPlaceHolder")}
                                                value={data?.contact ?? ""}
                                                debounce
                                                multiline
                                                rows={8}
                                                errorText={
                                                    props.showError && isNullOrWhitespace(data?.contact ?? undefined)
                                                        ? translateString("fieldRequired")
                                                        : undefined
                                                }
                                                disabled={disableMandatoryFields}
                                            />
                                        </TextBoxWrapper>
                                    )}
                                </Fragment>
                            </Grid>
                        </Grid>
                    </div>
                </Box>
            )}
        </div>
    );
}
